import React from 'react';
import { useIntl } from 'react-intl';
import SupportChatExt, { type AuthService, type RegisterMessageData } from '@dh-fe/react-support-chat';
import Html from 'components/html';
import Icon, { IconList } from 'components/icon';
import IconHover from 'components/icon-hover';
import application from 'services/application';
import { noopFn } from 'utils/noop';

import type { ColorConfig } from './contracts';
import messages from './support-chat.i18n';
import { testId } from './support-chat.settings';
import { getColorMap, getHeaderMessage } from './utils';
import styles from './support-chat.module.scss';

interface SupportChatElement {
  config: RegisterMessageData;
  socketUrl: string;
  onGetAuthToken: AuthService;
  onFirstSupportMessageReceived: (nick: string | undefined, conversationId: string | undefined) => void;
  onKicked: () => void;
}

const SupportChat: React.FunctionComponent<SupportChatElement> = (props) => {
  const { config, socketUrl, onGetAuthToken, onFirstSupportMessageReceived, onKicked } = props;
  const [colorConfig, setColorConfig] = React.useState<ColorConfig>();
  const containerRef = React.useRef<HTMLDivElement>(undefined);
  const translations = messages.pages.landing.onlineSupport.supportChat;
  const { formatMessage } = useIntl();
  const headerText = getHeaderMessage(formatMessage, config.lang);

  React.useLayoutEffect(() => {
    if (!containerRef.current) return;

    const colors = getColorMap(containerRef.current);

    setColorConfig(colors);
  }, []);

  const supportChat = React.useMemo(() => {
    if (colorConfig === undefined) return null;

    return (
      <SupportChatExt
        config={config}
        translations={{
          headerText,
          buttonText: formatMessage(translations.component.sendButton),
          connectionFailedText: formatMessage(translations.component.connectionFailed),
          duplicateConnectionText: formatMessage(translations.component.duplicateConnection),
          initializingText: formatMessage(translations.component.initializing),
          placeholderText: formatMessage(translations.component.placeholder),
          typingText: formatMessage(translations.component.typing),
          userKickedText: formatMessage(translations.component.userKicked),
          userNickText: formatMessage(translations.component.userNick),
          supportNickSuffix: formatMessage(translations.component.customerSupport),
          welcomeText: formatMessage(translations.component.welcome),
        }}
        socketUrl={socketUrl}
        getAuthToken={onGetAuthToken}
        onFirstSupportMessageReceived={onFirstSupportMessageReceived}
        onKicked={onKicked}
        theme={{
          colors: colorConfig,
          components: {
            ZoomContainer: noopFn,
            SubmitButton: (submitProps: unknown) => {
              const { disabled } = submitProps as HTMLButtonElement;

              return (
                <Html.button className={styles.sendButton} disabled={disabled} type="submit">
                  {!disabled && (
                    <IconHover active={IconList.sendSolid} inactive={IconList.sendOutline} className="px-1" />
                  )}
                  {disabled && <Icon className="px-1" name={IconList.sendOutline} />}
                </Html.button>
              );
            },
          },
        }}
        defaultFontSize={14}
        showHeader={application.LiveJasmin}
        showAdditionalMessages={application.LiveJasmin}
      />
    );
  }, [colorConfig, config, socketUrl, onGetAuthToken, onFirstSupportMessageReceived, onKicked, formatMessage]);

  return (
    <Html.div className={[styles.supportChat, 'h-100']} testId={testId.component} ref={containerRef}>
      {supportChat}
    </Html.div>
  );
};

export type { SupportChatElement };

export default React.memo(SupportChat, (prev, next) => {
  return prev.socketUrl === next.socketUrl;
});
